import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  InputComponent,
  InputErrorsComponent,
  MbsPopupType,
  ModalService,
  ModalSettings,
  SelectComponent,
  SwitcherComponent
} from 'mbs-ui-kit';
import { toValidVmName } from 'mbs-ui-shared@utils/vm-name.utils';
import { noop } from 'rxjs';

export type VmNameFieldMode = 'hyperV' | 'proxmox';

export type VmNameFieldMachine = {
  id?: string;
  isCustom?: boolean;
  name: string;
};

export type HyperVVmNameFieldValue = {
  kind: 'hyperV';
  name: string;
  computerName?: string;
};

export type ProxmoxVmNameFieldValue = {
  id: string;
  isCustom?: boolean;
  kind: 'proxmox';
  name: string;
  computerName?: string;
};

export type VmNameFieldValue = HyperVVmNameFieldValue | ProxmoxVmNameFieldValue;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    FormsModule,
    AlertComponent,
    InputComponent,
    InputErrorsComponent,
    I18NextPipe,
    NgbTooltip,
    NgTemplateOutlet,
    SelectComponent,
    SwitcherComponent
  ],
  selector: 'mbs-vm-name-field',
  standalone: true,
  templateUrl: './vm-name-field.component.html'
})
export class VmNameFieldComponent {
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);

  public readonly mbsPopupType = MbsPopupType;

  public readonly appendTo = input<string>('.modal-content');
  public readonly disabled = input<boolean>(false);
  public readonly editValidationError = input<((value: string) => string | null) | null>(null);
  public readonly inline = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly machines = input<ReadonlyArray<VmNameFieldMachine>>([]);
  public readonly mode = input<VmNameFieldMode>('hyperV');
  public readonly overwrite = input<boolean>(false);
  public readonly selectId = input<string>('vm-name-select');
  public readonly selectMaxWidth = input<string>('564px');
  public readonly switcherId = input<string>('vm-settings-overwrite-switcher');
  public readonly value = input<VmNameFieldValue | null>(null);

  public readonly overwriteChange = output<boolean>();
  public readonly validityChange = output<boolean>();
  public readonly valueChange = output<VmNameFieldValue>();

  private readonly customMachine = signal<VmNameFieldMachine | null>(null);

  public readonly editNameValue = signal('');
  public readonly editNameTouched = signal(false);

  public readonly isProxmoxMode = computed(() => this.mode() === 'proxmox');
  public readonly selectBindValue = computed(() => (this.isProxmoxMode() ? 'id' : 'name'));
  private readonly instantRestoreName = computed(() => {
    const computerName = this.normalizeNameValue(this.value()?.computerName).trim();

    if (!computerName) return '';

    return toValidVmName(
      this.i18nPipe.transform('shared:sidepanel-backup.backupStorages.browseStorage.tables.instantlyRestoreVmNameDefault', {
        computerName
      }),
      this.mode()
    );
  });
  private readonly instantRestoreMachine = computed<VmNameFieldMachine | null>(() => {
    const machines = this.normalizeMachines(this.machines());
    const name = this.instantRestoreName();

    if (!name || this.loading()) return null;

    const existingMachine = machines.find((machine) => machine.name === name && !machine.isCustom);

    if (existingMachine) return existingMachine;

    if (!this.isProxmoxMode()) return { name, isCustom: true };

    return { id: this.encodeNumberToGuid(this.getNextProxmoxMachineNumber(machines)), name, isCustom: true };
  });
  public readonly selectOptions = computed(() => {
    const machines = this.normalizeMachines(this.machines());
    const currentMachine = this.getCurrentValueMachine();
    const customMachine = this.customMachine() || this.instantRestoreMachine();

    return this.appendMissingMachine(this.upsertCustomMachine(machines, customMachine), currentMachine);
  });
  private readonly selectedMachine = computed(() => this.getSelectedMachine());
  private readonly resolvedValue = computed(() => this.getValueFromMachine(this.selectedMachine()));

  public readonly selectedSelectValue = computed(() => {
    const machine = this.selectedMachine();

    return this.isProxmoxMode() ? machine?.id || '' : machine?.name || '';
  });
  public readonly needOverwrite = computed(() => {
    if (this.overwrite()) return false;

    const value = this.value();
    const targetValue =
      this.isProxmoxMode() && value?.kind === 'proxmox' ? this.normalizeNameValue(value.id) : this.normalizeNameValue(value?.name);

    if (!targetValue) return false;

    return this.normalizeMachines(this.machines()).some((machine) => {
      if (machine.isCustom) return false;

      return this.isProxmoxMode() ? machine.id === targetValue : machine.name === targetValue;
    });
  });
  public readonly editNameError = computed(() => {
    const value = this.normalizeNameValue(this.editNameValue()).trim();

    if (!value) return 'The field is required';

    return this.editValidationError()?.(value) || null;
  });
  public readonly editNameErrors = computed(() =>
    this.editNameTouched() && this.editNameError() ? [{ message: this.editNameError() }] : []
  );
  public readonly editNameInvalid = computed(() => !!this.editNameError());

  private readonly changeNameModal = viewChild('changeNameModal', { read: TemplateRef });
  private readonly editNameInvalid$ = toObservable(this.editNameInvalid);

  constructor() {
    effect(() => {
      const resolvedValue = this.resolvedValue();

      if (!resolvedValue || this.isSameValue(resolvedValue, this.value())) return;

      queueMicrotask(() => {
        if (!this.isSameValue(resolvedValue, this.value())) this.valueChange.emit(resolvedValue);
      });
    });
    effect(() => {
      const valid = !this.needOverwrite();

      queueMicrotask(() => this.validityChange.emit(valid));
    });
  }

  public openEditModal(): void {
    if (this.disabled()) return;

    const modal = this.changeNameModal();
    const initialName = this.resolvedValue()?.name || this.normalizeNameValue(this.value()?.name);

    if (!modal) return;

    this.editNameValue.set(initialName);
    this.editNameTouched.set(false);

    this.modalService
      .open(this.getChangeNameSettings(), modal)
      .then((confirm) => {
        if (!confirm) return;

        const newName = this.normalizeNameValue(this.editNameValue()).trim();

        if (newName === initialName) return;

        this.emitValueFromEditedName(newName);
      })
      .catch(noop);
  }

  public overwriteChangeHandler(value: boolean): void {
    this.overwriteChange.emit(value);
  }

  public machineSelectChangeHandler(value: unknown): void {
    const machine = this.findMachineBySelectValue(value);
    const vmValue = this.getValueFromMachine(machine);

    if (vmValue) this.valueChange.emit(vmValue);
  }

  public editNameChangeHandler(value: unknown): void {
    this.editNameTouched.set(true);
    this.editNameValue.set(this.normalizeNameValue(value));
  }

  private getChangeNameSettings(): ModalSettings {
    return {
      header: { title: this.i18nPipe.transform('wizards:editVMNameTitle') },
      footer: {
        okButton: {
          text: this.i18nPipe.transform('shared:buttons.save', { format: 'title' }),
          disabled$: this.editNameInvalid$
        },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel', { format: 'title' }) }
      }
    };
  }

  private emitValueFromEditedName(name: string): void {
    if (!this.isProxmoxMode()) {
      this.customMachine.set({ name, isCustom: true });
      this.valueChange.emit({ kind: 'hyperV', name });
      return;
    }

    const existingMachine = this.selectOptions().find((machine) => machine.name === name && !machine.isCustom);
    const customMachine = existingMachine || this.getCustomProxmoxMachine(name);

    this.customMachine.set(customMachine.isCustom ? customMachine : null);
    this.valueChange.emit({
      kind: 'proxmox',
      id: customMachine.id || '',
      name: customMachine.name,
      isCustom: customMachine.isCustom
    });
  }

  private getCustomProxmoxMachine(name: string): VmNameFieldMachine {
    const customMachine = this.selectOptions().find((machine) => machine.isCustom);
    const id = customMachine?.id || this.encodeNumberToGuid(this.getNextProxmoxMachineNumber(this.selectOptions()));

    return { id, name, isCustom: true };
  }

  private getNextProxmoxMachineNumber(machines: ReadonlyArray<VmNameFieldMachine>): number {
    const maxId = machines.reduce((max, machine) => {
      const id = this.decodeGuidToNumber(machine.id);

      return id > max ? id : max;
    }, 0);

    return maxId + 1;
  }

  private getSelectedMachine(): VmNameFieldMachine | null {
    const options = this.selectOptions();
    const value = this.value();
    const currentName = this.normalizeNameValue(value?.name);
    const defaultMachine = this.getDefaultMachine(options);

    if (this.isProxmoxMode()) {
      const currentId = value?.kind === 'proxmox' ? this.normalizeNameValue(value.id) : '';
      // A custom id is only the next free number at the time it was generated, so it follows the list once the machines load
      const currentCustomMachine =
        value?.kind === 'proxmox' && value.isCustom && currentName
          ? options.find((machine) => machine.isCustom && machine.name === currentName)
          : null;

      return (
        currentCustomMachine ||
        (currentId ? options.find((machine) => machine.id === currentId) : null) ||
        (currentName ? options.find((machine) => machine.name === currentName) : null) ||
        defaultMachine
      );
    }

    return (currentName ? options.find((machine) => machine.name === currentName) : null) || defaultMachine;
  }

  private getDefaultMachine(options: VmNameFieldMachine[]): VmNameFieldMachine | null {
    const machine = this.customMachine() || this.instantRestoreMachine();

    return machine?.name ? machine : options[0] || null;
  }

  private findMachineBySelectValue(value: unknown): VmNameFieldMachine | null {
    const normalizedValue = this.normalizeNameValue(value);

    if (!normalizedValue) return null;

    return (
      this.selectOptions().find((machine) =>
        this.isProxmoxMode() ? machine.id === normalizedValue || machine.name === normalizedValue : machine.name === normalizedValue
      ) || (this.isProxmoxMode() ? null : { name: normalizedValue })
    );
  }

  private getValueFromMachine(machine: VmNameFieldMachine | null): VmNameFieldValue | null {
    if (!machine?.name) return null;

    if (!this.isProxmoxMode()) return { kind: 'hyperV', name: machine.name };

    return {
      kind: 'proxmox',
      id: machine.id || '',
      name: machine.name,
      isCustom: machine.isCustom
    };
  }

  private getCurrentValueMachine(): VmNameFieldMachine | null {
    const value = this.value();
    const name = this.normalizeNameValue(value?.name);

    if (!name) return null;

    if (this.isProxmoxMode()) {
      return value?.kind === 'proxmox' && value.id
        ? { id: this.normalizeNameValue(value.id), name, isCustom: value.isCustom ?? true }
        : null;
    }

    return { name };
  }

  private normalizeMachines(machines: ReadonlyArray<VmNameFieldMachine>): VmNameFieldMachine[] {
    return (machines || [])
      .map((machine) => ({
        id: this.normalizeNameValue(machine?.id),
        isCustom: machine?.isCustom,
        name: this.normalizeNameValue(machine?.name)
      }))
      .filter((machine) => !!machine.name);
  }

  private appendMissingMachine(machines: VmNameFieldMachine[], machine: VmNameFieldMachine | null): VmNameFieldMachine[] {
    if (!machine?.name) return machines;

    const exists = machine.id ? machines.some((item) => item.id === machine.id) : machines.some((item) => item.name === machine.name);

    return exists ? machines : [...machines, machine];
  }

  private upsertCustomMachine(machines: VmNameFieldMachine[], customMachine: VmNameFieldMachine | null): VmNameFieldMachine[] {
    if (!customMachine?.name) return machines;

    const existingIndex = customMachine.id
      ? machines.findIndex((machine) => machine.id === customMachine.id)
      : machines.findIndex((machine) => machine.isCustom || machine.name === customMachine.name);

    if (existingIndex === -1) return [...machines, customMachine];

    return machines.map((machine, index) => (index === existingIndex ? customMachine : machine));
  }

  private isSameValue(left: VmNameFieldValue, right: VmNameFieldValue | null): boolean {
    if (!right || left.kind !== right.kind || left.name !== right.name) return false;

    return left.kind !== 'proxmox' || (right.kind === 'proxmox' && left.id === right.id);
  }

  private normalizeNameValue(value: unknown): string {
    const valueType = typeof value;

    if (valueType === 'string') return value as string;
    if (valueType === 'number' || valueType === 'boolean') return String(value);
    if (valueType !== 'object' || value === null) return '';

    const record = value as Record<string, unknown>;
    const target = record.target;

    if (this.isRecord(target)) return this.normalizeNameValue(target.value);

    return this.normalizeNameValue(record.name ?? record.label ?? record.value);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private encodeNumberToGuid(value: number): string {
    const safeValue = Number.isInteger(value) && value > 0 ? value : 1;
    const hex = safeValue.toString(16).padStart(8, '0');

    return `${hex}-0000-0000-0000-000000000000`;
  }

  private decodeGuidToNumber(value: string): number {
    const uuid = this.normalizeNameValue(value);
    const parts = uuid.split('-');

    if (parts.length !== 5) return 0;

    const isEncodedNumber = parts.slice(1).every((part) => /^0+$/.test(part)) && !/^0+$/.test(parts[0]);

    return isEncodedNumber ? parseInt(parts[0], 16) || 0 : 0;
  }
}
